import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ATTRAPPE_MELDUNGSEINSTELLUNGEN } from "./attrappe/meldungen";
import { ATTRAPPE, aendere, ersetze, hole, loesche, sende } from "./klient";
import type { Form, Stufe } from "./faelle";

/* ══════════════════════════════════════════════════════════════════
   Die Meldungen — Glocke, eigene Einstellungen, Pflege (24.09.2026,
   Migration 0176, 51-meldungen.md).

   Die Glocke zeigt, **was sich für mich geändert hat** — nicht, was
   anliegt (das zeigt die Startseite). Gelesen gilt auf allen Geräten, weil
   es an der Meldung in der Datenbank steht und nicht im Browser.
   ══════════════════════════════════════════════════════════════════ */

export type Meldung = {
  id: string;
  art: string;
  form: Form;
  stufe: Stufe;
  data: {
    kennung?: string | null; kunde?: string | null; vorgang?: string | null; weg?: string | null;
    werte?: Record<string, string>; faellig?: string | null;
  };
  created_at: string;
  read_at: string | null;
  resolved_at: string | null;
};
export type Glockenstand = { eintraege: Meldung[]; ungelesen: number; blockiert: boolean };

const vorhin = (minuten: number) => new Date(Date.now() - minuten * 60_000).toISOString();
const ATTRAPPE_GLOCKE: Glockenstand = {
  ungelesen: 3,
  blockiert: true,
  eintraege: [
    { id: "m1", art: "defekt_draussen", form: "aufgabe", stufe: "blockiert",
      data: { kennung: "RP-02", kunde: "Musterfirma GmbH", vorgang: "V-2026-0016", weg: "/kalender" },
      created_at: vorhin(35), read_at: null, resolved_at: null },
    { id: "m2", art: "unterschrift_fehlt", form: "entscheidung", stufe: "heute",
      data: { kennung: "AB-22", kunde: "Beispiel GmbH", vorgang: "V-2026-0014", weg: "/auftraege/V-2026-0014",
        werte: { grund: "Kunde nicht vor Ort" } },
      created_at: vorhin(80), read_at: null, resolved_at: null },
    { id: "m3", art: "zugeteilt", form: "aufgabe", stufe: "heute",
      data: { kennung: "KR-01", kunde: "Musterfirma GmbH", vorgang: "V-2026-0015", weg: "/auftraege/V-2026-0015",
        werte: { aufgabe: "vorbereitung" } },
      created_at: vorhin(140), read_at: null, resolved_at: null },
    { id: "m4", art: "ruecknahme", form: "aufgabe", stufe: "ueberfaellig",
      data: { kennung: "RP-05", kunde: "Bau Schmitz", vorgang: "V-2026-0011", weg: "/auftraege/V-2026-0011" },
      created_at: vorhin(1500), read_at: vorhin(1400), resolved_at: vorhin(1300) },
  ],
};

export const glockenSchluessel = ["meldungen", "glocke"] as const;

export const useGlocke = () =>
  useQuery({
    queryKey: glockenSchluessel,
    queryFn: () => (ATTRAPPE ? Promise.resolve(ATTRAPPE_GLOCKE) : hole<Glockenstand>("/meldungen")),
    /* Schnell (Luca, 24.09.2026): alle 15 Sekunden, sofort beim Zurückkehren
       ins Fenster und sofort, wenn ein Push kommt (`mo-neu` aus sw.js). */
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

export function useGelesen() {
  const abfragen = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) => (ATTRAPPE
      ? Promise.resolve({ gelesen: ids?.length ?? 0 })
      : sende<{ gelesen: number }>("/meldungen/gelesen", ids ? { ids } : {})),
    onSuccess: () => void abfragen.invalidateQueries({ queryKey: glockenSchluessel }),
  });
}

/* ══════════════ Für mich ══════════════ */

/** Ein Gerät mit Push-Abo (0176, 0181). */
export type Pushgeraet = {
  id: string; label: string | null; user_agent: string | null; created_at: string;
  last_success_at: string | null; last_received_at: string | null; failed_at: string | null; endpoint: string;
  staff_id?: string; name?: string;
};

export type MeineKanaele = {
  kanaele: { kanal: "push" | "mail"; an: boolean; ruhe_von: string | null; ruhe_bis: string | null }[];
  geraete: Pushgeraet[];
  mail: string | null;
};

export const useMeineMeldungseinstellungen = (offen: boolean) =>
  useQuery({
    queryKey: ["meldungen", "ich"],
    queryFn: () => (ATTRAPPE
      ? Promise.resolve<MeineKanaele>({ kanaele: [], geraete: [], mail: "lisa@mr-machine.de" })
      : hole<MeineKanaele>("/meldungen/ich")),
    enabled: offen,
  });

export function useMeineMeldungseinstellungenAendern() {
  const abfragen = useQueryClient();
  return useMutation({
    /* Nur die Ruhezeit — alles andere stellt die Verwaltung ein (24.09.2026). */
    mutationFn: (z: { ruhe_von: string | null; ruhe_bis: string | null }) =>
      (ATTRAPPE ? Promise.resolve(z) : ersetze("/meldungen/ich", z)),
    onSuccess: () => void abfragen.invalidateQueries({ queryKey: ["meldungen", "ich"] }),
  });
}

/* ══════════════ Pflege ══════════════ */

export type Gruppe = {
  id: string; code: string; label: string; label_key: string | null; sort_order: number; active: boolean;
  mitglieder: { id: string; role: string | null; staff_id: string | null; name: string | null; excluded: boolean }[];
};
export type Katalogzeile = {
  art: string;
  form: Form;
  vorgabe: { gruppe: string; eskalation: string | null; grenzen: Record<string, number | string> };
  wirksam: { gruppe: string; eskalation: string | null; an: boolean; grenzen: Record<string, number | string> };
};
export type Kanalzeile = { art: string | null; stufe: Stufe; kanal: "app" | "push" | "mail"; an: boolean };
export type Meldungseinstellungen = {
  gruppen: Gruppe[];
  katalog: Katalogzeile[];
  kanaele: Kanalzeile[];
  vorlage: { an: boolean; uhrzeit: string; betreff: string | null; einleitung: string | null; abschnitte: string[] } | null;
  leute: { id: string; name: string }[];
  personen: { id: string; name: string; konto: boolean; app: boolean; push: boolean; mail: boolean; geraete: number }[];
  rollen: { code: string; label: string; label_key: string | null }[];
};

const einstSchluessel = ["meldungen", "einstellungen"] as const;

export const useMeldungseinstellungen = () =>
  useQuery({
    queryKey: einstSchluessel,
    queryFn: () => (ATTRAPPE
      ? Promise.resolve<Meldungseinstellungen>(ATTRAPPE_MELDUNGSEINSTELLUNGEN)
      : hole<Meldungseinstellungen>("/meldungen/einstellungen")),
  });

function useAenderung<T>(fn: (x: T) => Promise<unknown>) {
  const abfragen = useQueryClient();
  return useMutation({
    mutationFn: (x: T) => (ATTRAPPE ? Promise.resolve(x) : fn(x)),
    onSuccess: () => {
      void abfragen.invalidateQueries({ queryKey: einstSchluessel });
      void abfragen.invalidateQueries({ queryKey: ["faelle"] });
    },
  });
}

export const useRegelAendern = () => useAenderung(
  (x: { art: string; gruppe_id: string | null; eskalation_id: string | null; an: boolean; grenzen: Record<string, number | string> }) =>
    ersetze(`/meldungen/regeln/${encodeURIComponent(x.art)}`, x),
);
export const usePersonKanalAendern = () => useAenderung(
  (x: { person: string; kanal: "app" | "push" | "mail"; an: boolean }) =>
    ersetze(`/meldungen/personen/${x.person}`, { kanal: x.kanal, an: x.an }),
);
export const useKanaeleAendern = () => useAenderung((zeilen: Kanalzeile[]) => ersetze("/meldungen/kanaele", { zeilen }));
export const useKanaeleZuruecksetzen = () => useAenderung((art: string) => loesche(`/meldungen/kanaele/${encodeURIComponent(art)}`));
export const useGruppeAnlegen = () => useAenderung((label: string) => sende("/meldungen/gruppen", { label }));
export const useGruppeAendern = () => useAenderung((x: { id: string; label?: string; aktiv?: boolean }) => aendere(`/meldungen/gruppen/${x.id}`, x));
export const useGruppeLoeschen = () => useAenderung((id: string) => loesche(`/meldungen/gruppen/${id}`));
export const useMitgliedAnlegen = () => useAenderung(
  (x: { gruppe: string; rolle?: string; person_id?: string; ausnehmen?: boolean }) => sende(`/meldungen/gruppen/${x.gruppe}/mitglieder`, x),
);
export const useMitgliedLoeschen = () => useAenderung((id: string) => loesche(`/meldungen/mitglieder/${id}`));
export const useVorlageAendern = () => useAenderung(
  (x: { an: boolean; uhrzeit: string; betreff: string | null; einleitung: string | null; abschnitte: string[] }) => ersetze("/meldungen/vorlage", x),
);

/* ══════════════ Test und Vorschau (24.09.2026) ══════════════ */

export type Testergebnis = {
  ok: boolean;
  kanal: "app" | "push" | "mail";
  grund?: "kein_schluessel" | "kein_geraet" | "keine_adresse" | "kein_mailzugang";
  geraete?: number; gesendet?: number; tot?: number;
  an?: string; fehler?: string | null;
};

/** Eine Testmeldung an eine Person — die Antwort sagt, was geschah. */
export const useTestSenden = () => useMutation({
  mutationFn: (x: { person: string; kanal: "app" | "push" | "mail" }) => (ATTRAPPE
    ? Promise.resolve<Testergebnis>({ ok: false, kanal: x.kanal, grund: "kein_schluessel" })
    : sende<Testergebnis>("/meldungen/test", x)),
});

/** Die Morgenmail, wie sie heute ankäme — für die Vorschau neben dem Editor. */
export const useMorgenmailVorschau = () => useMutation({
  mutationFn: (x: { betreff: string | null; einleitung: string | null; abschnitte: string[] }) => (ATTRAPPE
    /* Ohne API kein Rahmen aus `meldungsmail.ts` — nur der Text, in der Schrift einer Mail. */
    ? Promise.resolve({
      betreff: x.betreff ?? "",
      html: `<div style="font-family:Helvetica,Arial,sans-serif;font-size:15px;line-height:1.5;color:#1f2937;background:#fff;padding:24px;max-width:600px;margin:24px auto;border-radius:10px">${x.einleitung ?? ""}</div>`,
      leer: false,
    })
    : sende<{ betreff: string; html: string; leer: boolean }>("/meldungen/vorlage/vorschau", x)),
});

/* ══════════════ Geräte (24.09.2026, 0181) ══════════════ */

export const useAlleGeraete = () => useQuery({
  queryKey: ["meldungen", "geraete"],
  queryFn: () => (ATTRAPPE
    ? Promise.resolve({ geraete: [] as Pushgeraet[] })
    : hole<{ geraete: Pushgeraet[] }>("/meldungen/geraete-alle")),
  refetchInterval: 20_000,
});

export function useGeraetLoeschen() {
  const abfragen = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => (ATTRAPPE ? Promise.resolve(id) : loesche(`/meldungen/geraete/${id}`)),
    onSuccess: () => {
      void abfragen.invalidateQueries({ queryKey: ["meldungen"] });
    },
  });
}

/** Ein Test-Push an die eigenen Geräte — ohne das Recht `verwalten`. */
export const useTestIch = () => {
  const abfragen = useQueryClient();
  return useMutation({
    mutationFn: () => (ATTRAPPE
      ? Promise.resolve<Testergebnis>({ ok: false, kanal: "push", grund: "kein_schluessel" })
      : sende<Testergebnis>("/meldungen/test-ich")),
    /* Nach ein paar Sekunden steht da, ob das Gerät den Empfang gemeldet hat. */
    onSuccess: () => { window.setTimeout(() => void abfragen.invalidateQueries({ queryKey: ["meldungen", "ich"] }), 4000); },
  });
};
